/**
 * The reseller theme: how a white-label customer's colours reach the app's styling.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE — the platform's own theme must never change. A reseller's
 * colours apply ONLY on that reseller's own domain, to that reseller and to their customers. On
 * qcall.ai (and on any host /branding does not recognise as a live reseller domain) nothing is set
 * at all, so the app renders exactly the colours it shipped with.
 *
 * It is enforced in two independent ways, so one of them failing is still safe:
 *
 *  1. applyResellerTheme() returns early unless /branding said `is_white_label` (see isWhiteLabel), and it
 *     REMOVES any variable it previously set when that is false. Switching domains in one tab, or a
 *     cached appSetting from another host, therefore cannot leak a reseller's colour onto the
 *     platform.
 *  2. Every variable is read with its platform value as the CSS fallback —
 *     `rgb(var(--brand-primary-rgb, 124 83 252) / <alpha-value>)`. If this file never runs (JS
 *     error, slow network, a page rendered before boot), the fallback is the shipped colour. The
 *     theme is an override on top of a complete default, never a dependency of it.
 *
 * Two variables carry the same colour because two styling systems consume it: Tailwind needs space-
 * separated RGB channels so `bg-primary/80` can still apply opacity (a var holding `#7C53FC` cannot
 * be given an alpha), while Chakra and plain CSS take the hex directly.
 */

/** Every variable this module owns. Also the list it clears on a platform host — see applyResellerTheme. */
const VARS = [
  "--brand-primary",
  "--brand-primary-rgb",
  "--brand-primary-contrast",
  "--brand-secondary",
  "--brand-secondary-rgb",
  "--brand-radius",
  "--brand-font",
  // The tool illustrations (assets/svg/callTransfer.svg and friends) are five tints of one blue.
  // Each tint is a variable with its original hex as the fallback, so an unthemed page renders them
  // exactly as before; here they are re-derived from the reseller's colour at the same lightness
  // steps, which keeps the artwork's depth instead of flattening it to one flat shape.
  "--tool-ink",
  "--tool-700",
  "--tool-500",
  "--tool-300",
  "--tool-100",
  // The brand ramp Tailwind's `purple-*` / `brand-*` classes read (tailwind.config.js). 210 usages
  // across 50 files are the product's accent colour; theming the ramp moves all of them at once.
  "--brand-50", "--brand-100", "--brand-200", "--brand-300", "--brand-400",
  "--brand-500", "--brand-600", "--brand-700", "--brand-800", "--brand-900",
] as const;

/** The colours the app ships with. Kept here only to compute contrast; the CSS fallbacks are the real defaults. */
export const PLATFORM_PRIMARY = "#7C53FC";
export const PLATFORM_SECONDARY = "#B951FB";

/**
 * Is this host a live white-label reseller domain?
 *
 * One reading of the flag for every caller, tolerant of only the shapes a boolean column really
 * arrives in over JSON: `true`, `1`, `"1"` and `"true"` mean yes; `false`, `0`, `"false"`, null,
 * undefined and a missing key all mean no.
 *
 * Both of the obvious one-liners are wrong in a way that is hard to see. A bare truthy test reads
 * the STRING "false" as yes, which would paint a reseller's colours onto a platform host. A bare
 * `=== true` silently ignores a backend that serialises tinyint(1) as 1 — and the symptom of that
 * is exactly "the reseller saved a colour and nothing happened", with no error anywhere.
 */
export function isWhiteLabel(value: unknown): boolean {
  if (value === true || value === 1) return true;
  return typeof value === "string" && ["1", "true"].includes(value.trim().toLowerCase());
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#7C53FC" -> [124, 83, 252]. Null for anything that is not a hex colour — never a silent black. */
export function hexToRgb(value: unknown): [number, number, number] | null {
  const raw = String(value ?? "").trim();
  if (!HEX.test(raw)) return null;
  let hex = raw.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

/** Relative luminance (WCAG 2.1), used for both the contrast ratio and the readable-text choice. */
function luminance([r, g, b]: [number, number, number]): number {
  const [R, G, B] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** WCAG contrast ratio between two hex colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number | null {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) return null;
  const [la, lb] = [luminance(ca), luminance(cb)];
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Black or white text for a button filled with this colour. A reseller who picks a pale yellow gets
 * black text rather than the white the platform's purple needs — otherwise their own primary button
 * becomes unreadable the moment they save.
 */
export function readableTextOn(hex: string): "#FFFFFF" | "#111111" {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#FFFFFF";
  return luminance(rgb) > 0.45 ? "#111111" : "#FFFFFF";
}

export type ResellerTheme = {
  primary_color?: string | null;
  secondary_color?: string | null;
  theme_font?: string | null;
  theme_radius?: string | null;
};

/** Fonts a reseller may choose. A fixed list, because an arbitrary family would need loading and could 404. */
export const THEME_FONTS: { value: string; label: string; stack: string }[] = [
  { value: "", label: "Default (DM Sans)", stack: "" },
  { value: "inter", label: "Inter", stack: "'Inter', system-ui, sans-serif" },
  { value: "roboto", label: "Roboto", stack: "'Roboto', system-ui, sans-serif" },
  { value: "poppins", label: "Poppins", stack: "'Poppins', system-ui, sans-serif" },
  { value: "system", label: "System", stack: "system-ui, -apple-system, 'Segoe UI', sans-serif" },
];

/** Corner roundness presets, applied through one variable the shared surfaces read. */
export const THEME_RADII: { value: string; label: string; css: string }[] = [
  { value: "", label: "Default", css: "" },
  { value: "sharp", label: "Sharp", css: "2px" },
  { value: "rounded", label: "Rounded", css: "10px" },
  { value: "pill", label: "Pill", css: "16px" },
];

/**
 * Apply (or clear) the reseller's theme.
 *
 * @param settings the /branding response. `is_white_label` decides everything: false or missing and
 *                 this clears every variable it owns, leaving the platform's shipped theme intact.
 */
export function applyResellerTheme(settings: (ResellerTheme & { is_white_label?: unknown }) | null | undefined): void {
  const root = document.documentElement;

  // Not a reseller domain -> remove anything a previous render may have set, and stop. This is the
  // guarantee that the platform's own theme is never touched.
  if (!settings || !isWhiteLabel(settings.is_white_label)) {
    VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }

  const set = (name: string, value: string | null) =>
    value ? root.style.setProperty(name, value) : root.style.removeProperty(name);

  const primary = hexToRgb(settings.primary_color) ? String(settings.primary_color).trim() : null;
  set("--brand-primary", primary);
  set("--brand-primary-rgb", primary ? hexToRgb(primary)!.join(" ") : null);
  // Text that sits ON the primary colour, so a light brand colour keeps its buttons readable.
  set("--brand-primary-contrast", primary ? readableTextOn(primary) : null);

  const secondary = hexToRgb(settings.secondary_color) ? String(settings.secondary_color).trim() : null;
  set("--brand-secondary", secondary);
  set("--brand-secondary-rgb", secondary ? hexToRgb(secondary)!.join(" ") : null);

  // Illustration tints: the brand colour composited over white at the steps the original artwork
  // used, so a line that was the darkest blue becomes the darkest brand tone and a pale fill stays
  // pale. Mixed here rather than with an alpha so the icons keep working on any background.
  if (primary) {
    const [r, g, b] = hexToRgb(primary)!;
    const mix = (pct: number) =>
      `rgb(${[r, g, b].map((c) => Math.round(c * pct + 255 * (1 - pct))).join(" ")})`;
    // The accent ramp, at the lightness steps the shipped palette used: 50 is a near-white tint,
    // 500 the colour itself, 900 a deep shade. Mixed with white above 500 and with black below, so
    // any brand colour produces a usable ramp rather than ten versions of the same flat tone.
    const RAMP: [string, number][] = [
      ["50", 0.05], ["100", 0.12], ["200", 0.25], ["300", 0.45], ["400", 0.7],
      ["500", 1], ["600", -0.18], ["700", -0.36], ["800", -0.54], ["900", -0.72],
    ];
    const shade = (p: number) =>
      p >= 0
        ? `${[r, g, b].map((c) => Math.round(c * p + 255 * (1 - p))).join(" ")}`
        : `${[r, g, b].map((c) => Math.round(c * (1 + p))).join(" ")}`;
    RAMP.forEach(([name, p]) => set(`--brand-${name}`, shade(p)));

    set("--tool-ink", mix(1));
    set("--tool-700", mix(0.72));
    set("--tool-500", mix(0.52));
    set("--tool-300", mix(0.3));
    set("--tool-100", mix(0.13));
  }

  set("--brand-radius", THEME_RADII.find((r) => r.value === settings.theme_radius)?.css || null);
  set("--brand-font", THEME_FONTS.find((f) => f.value === settings.theme_font)?.stack || null);
}
